import {useOutletContext} from "react-router-dom";

import {useCheckPermission} from "../../hooks/permissionHooks";
import {type AppreciationKey, type AppreciationKeyObj, serializeKey} from "../../redux/appreciationsUtil";
import {selectPurposePolicy} from "../../redux/persistenceSlice";
import {useRevaSelector} from "../../redux/revaHooks";
import {PERMISSION_RULE_APPRECIATIONS, PERMISSION_RULE_DOC_LIMITED_SCOPE} from "../../util/permissions";

/**
 * Appreciation keys that are always shown — regardless of the appreciation permission (#18607).
 * Reasons: the Grundbuch free-text fields (#18607, extended for Zuwegung/Baurecht by #19004) and
 * the Konvergenzwert explanations are explicitly exempted by the ticket, and the energy /
 * Abteilung-II texts ship a pre-filled "TODO!" default that every user must be able to edit
 * (#18607 note-13).
 */
const APPRECIATION_ALWAYS_VISIBLE_KEYS = new Set([
    serializeKey({page: "object", tab: "register", section: "baulasten"}),
    serializeKey({page: "object", tab: "register", section: "altlasten"}),
    serializeKey({page: "object", tab: "register", section: "sonstige"}),
    serializeKey({page: "object", tab: "register", section: "zuwegung"}),
    serializeKey({page: "object", tab: "register", section: "baurecht"}),
    serializeKey({page: "object", tab: "energy"}),
    serializeKey({page: "object", tab: "register", section: "abteilung_ii"}),
]);

/**
 * #18687: Würdigungen ohne Druckstelle (Lärm, Klimarisiken) sind komplett ausgeblendet — Eingabefelder, Icons
 * und Zeilen im Tab Würdigungen —, bis feststeht, wo der Text im PDF erscheint. Gilt vor jeder Berechtigung.
 * Bereits gespeicherte Texte bleiben unverändert in den Dokument-Properties.
 */
const APPRECIATION_HIDDEN_KEYS = new Set([
    serializeKey({page: "location", tab: "noise"}),
    serializeKey({page: "location", tab: "climaterisk"}),
]);

/**
 * #18687: Ob die Würdigung mit diesem Key bewusst ausgeblendet ist.
 * @param appreciationKey Key als Objekt oder serialisiert
 */
export const isAppreciationHidden = (appreciationKey: AppreciationKey | null | undefined): boolean =>
    APPRECIATION_HIDDEN_KEYS.has(serializeKey(appreciationKey, true));

/** Erläuterungen im Tab Konvergenzwert: druckt nur die kompakte Vereinfachte Wertermittlung, daher an deren Recht gebunden (#18408). */
const EXPLANATIONS_KEY = serializeKey({page: "value", section: "explanations"});

/** #18408: Ob die Person die Erläuterungen zur Wertermittlung sieht — nur mit dem Recht für die kompakte Fassung. */
export const useValueExplanationsVisible = (): boolean => {
    const allowedByPurpose = useAppreciationsAllowedByPurpose();
    const hasCompactRight = useCheckPermission(PERMISSION_RULE_DOC_LIMITED_SCOPE);
    return allowedByPurpose && hasCompactRight;
};

/**
 * #19481: Ob der Zweck der geladenen REVA-ID Würdigungstexte kennt (`appreciationsAllowed`, bei Research nicht).
 * Gilt vor jeder Berechtigung und auch für die immer sichtbaren Ausnahmefelder.
 * @return whether the purpose of the loaded valuation allows appreciations
 */
export const useAppreciationsAllowedByPurpose = (): boolean => {
    return useRevaSelector(selectPurposePolicy).appreciationsAllowed;
};

/**
 * Central permission gate for the appreciation feature (#18607): the whole appreciation UI
 * (the Prüfung > Würdigungen tab, toolbar buttons, section icons, inputs and overlays) is only
 * available to users with PERMISSION_RULE_APPRECIATIONS (reva-doc-full-pfandbg-16 OR
 * reva-doc-full-belwertv-24, admins included) — and only if the purpose allows it (#19481).
 * @return whether the current user may see the appreciation UI at all
 */
export const useAppreciationsVisible = (): boolean => {
    const allowedByPurpose = useAppreciationsAllowedByPurpose();
    const hasPermission = useCheckPermission(PERMISSION_RULE_APPRECIATIONS);
    return allowedByPurpose && hasPermission;
};

/**
 * Per-field visibility gate for an individual appreciation element (#18607): visible when the purpose
 * allows appreciations (#19481) AND (the user has the appreciation permission OR the given key is one
 * of the always-visible exceptions, APPRECIATION_ALWAYS_VISIBLE_KEYS). The shared appreciation
 * components call this with their resolved key and render null when it returns false.
 * @param appreciationKey resolved appreciation key of the element
 * @return whether this specific appreciation element may be shown
 */
export const useAppreciationVisible = (appreciationKey: AppreciationKey | null | undefined): boolean => {
    const allowedByPurpose = useAppreciationsAllowedByPurpose();
    const hasPermission = useCheckPermission(PERMISSION_RULE_APPRECIATIONS);
    const hasCompactRight = useCheckPermission(PERMISSION_RULE_DOC_LIMITED_SCOPE);
    const key = serializeKey(appreciationKey, true);
    if (APPRECIATION_HIDDEN_KEYS.has(key)) return false;
    if (key === EXPLANATIONS_KEY) return allowedByPurpose && hasCompactRight;
    return allowedByPurpose && (hasPermission || APPRECIATION_ALWAYS_VISIBLE_KEYS.has(key));
};

/**
 * Resolve appreciation key from either an explicit appreciation key string, or from individual properties for page, optional tab, optional section.
 * @param appreciationKey the explicit appreciation key (can be empty)
 * @param page the page (can be empty)
 * @param tab the optional tab (can be empty)
 * @param section the optional section (can be empty)
 * @return resolved appreciation key, or undefined when neither source names one
 */
export const resolveAppreciationId = (
    appreciationKey: AppreciationKey | undefined,
    page: string | undefined,
    tab?: string,
    section?: string,
): AppreciationKey | undefined => {
    return page ? {page, tab, section} : appreciationKey;
};

/** Appreciation part of the outlet context, as PageObject, PageLocation and PageMarket provide it. */
interface AppreciationOutletContext {
    appreciation?: AppreciationKeyObj & {visible?: boolean};
}

/**
 * Use visibility state for an appreciation input, either from an explicitly given visible flag, or from the outlet context.
 * @param appreciationKey appreciation key associated with the input
 * @param visible explicit visibly flag override
 * @return resulting visibility for the given appreciation input
 */
export const useAppreciationVisibilityFromContext = (
    appreciationKey: AppreciationKey | null | undefined,
    visible?: boolean,
): boolean => {
    const {appreciation: appreciationStateFromContext} = useOutletContext<AppreciationOutletContext>();
    if (typeof visible === "boolean") {
        return visible;
    }
    if (typeof appreciationStateFromContext === "object") {
        const appreciationKeySerialFromContext = serializeKey(appreciationStateFromContext, true);
        const appreciationKeySerial = serializeKey(appreciationKey, true);
        if (appreciationKeySerialFromContext === appreciationKeySerial) {
            return !!appreciationStateFromContext.visible;
        }
    }
    return false;
};
